import { Link } from "wouter";
import {
  ArrowRight,
  Building2,
  Scale,
  Briefcase,
  Calculator,
  Umbrella,
  HardHat,
  ShieldCheck,
  Mail,
  AlertTriangle,
  Eye,
} from "lucide-react";
import { Seo } from "@/components/Seo";
import { Section, Eyebrow } from "@/components/Section";
import { WordCycler } from "@/components/WordCycler";
import { HeroProductMockup } from "@/components/landing/HeroProductMockup";
import { ConnectIDFlow } from "@/components/landing/ConnectIDFlow";
import { EmailBeforeAfter } from "@/components/landing/EmailBeforeAfter";
import { OperationalBenefits } from "@/components/landing/OperationalBenefits";
import { APP_SIGN_UP_URL } from "@/lib/utils";
import { INDUSTRIES, GENERIC_INDUSTRY } from "@/lib/industries";

const CONNECTID_LOGO = `${import.meta.env.BASE_URL}images/connectid/connectid-primary-brand-mark.png`;

const INDUSTRY_ICONS: Record<string, typeof Building2> = {
  "real-estate": Building2,
  conveyancers: HardHat,
  "law-firms": Scale,
  payroll: Briefcase,
  accountants: Calculator,
  insurance: Umbrella,
};

const FRAUD_TYPES = [
  {
    Icon: Mail,
    title: "Business Email Compromise",
    body: "Attackers infiltrate or spoof email threads, impersonating clients or suppliers to redirect payment changes to fraudulent accounts.",
  },
  {
    Icon: AlertTriangle,
    title: "Domain Spoofing",
    body: "Look-alike domains mirror trusted suppliers — fraudulent change requests land in inboxes and bypass standard spam filters.",
  },
  {
    Icon: Eye,
    title: "Man-in-the-Middle Interception",
    body: "Account details are silently altered in transit before they reach your accounts team, leaving no trace until funds are gone.",
  },
];

export default function Home() {
  return (
    <>
      <Seo
        title="FortifyID"
        description="Identity-verified change requests for any business that captures sensitive details from clients — real estate, conveyancing, legal, payroll, accounting, insurance and beyond. Hosted in Australia."
        path="/"
      />

      {/* Hero */}
      <Section className="!pt-8 sm:!pt-20 !pb-8">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Eyebrow>Identity-verified change requests</Eyebrow>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] text-[hsl(var(--brand-charcoal))]">
              Stop payment-redirection fraud for{" "}
              <span className="text-[hsl(var(--brand-forest))]">
                <WordCycler />
              </span>
            </h1>
            <p className="mt-6 text-lg text-[hsl(var(--brand-slate))] max-w-xl leading-relaxed">
              FortifyID is the verified change-request platform for any business
              that collects sensitive details from clients. Stop payment
              redirection fraud, eliminate data sprawl, enforce segregation of
              duties, and make client onboarding seamless — all in one auditable
              workflow.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={APP_SIGN_UP_URL}
                className="inline-flex items-center gap-2 bg-[hsl(var(--brand-forest))] text-[hsl(var(--brand-bone))] px-6 py-3 rounded font-medium hover:bg-[hsl(168,25%,22%)] transition-colors"
                data-testid="button-hero-signup"
              >
                Create your workspace <ArrowRight className="w-4 h-4" />
              </a>
              <Link
                href="/how-it-works"
                className="inline-flex items-center gap-2 border border-[hsl(var(--brand-sand))] bg-white/40 text-[hsl(var(--brand-charcoal))] px-6 py-3 rounded font-medium hover:bg-white/70 transition-colors"
                data-testid="button-hero-how"
              >
                See how it works
              </Link>
            </div>
            <div className="mt-8 font-mono text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--brand-slate))]">
              Built in Australia · Client data stored in Australia
            </div>
          </div>
          <div className="lg:col-span-5">
            <HeroProductMockup />
          </div>
        </div>
      </Section>

      {/* The Problem */}
      <Section className="!py-20 border-t border-[hsl(var(--brand-sand))]">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left: copy + fraud types */}
          <div>
            <Eyebrow>The problem</Eyebrow>
            <h2 className="mt-3 text-3xl sm:text-4xl font-semibold text-[hsl(var(--brand-charcoal))]">
              Email is not a payment control.
            </h2>
            <p className="mt-4 text-lg text-[hsl(var(--brand-slate))] leading-relaxed">
              Business email compromise, domain spoofing and man-in-the-middle
              attacks let fraudsters intercept or impersonate legitimate change
              requests. A bank account update that looks genuine in your inbox
              may have been altered in transit — and by the time funds are
              redirected, the trail has gone cold.
            </p>
            <div className="mt-10 flex flex-col gap-7">
              {FRAUD_TYPES.map(({ Icon, title, body }) => (
                <div key={title} className="flex gap-4">
                  <div className="w-9 h-9 rounded bg-[hsl(0_35%_45%)]/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Icon className="w-4 h-4 text-[hsl(0_35%_45%)]" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-[hsl(var(--brand-charcoal))]">{title}</h3>
                    <p className="mt-1 text-sm text-[hsl(var(--brand-slate))] leading-relaxed">{body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: annotated email mockup */}
          <div className="flex flex-col">
            <div className="flex items-center gap-2 mb-3">
              <div className="flex items-center gap-2 border border-dashed border-[hsl(0_35%_45%)]/30 bg-[hsl(0_35%_45%)]/5 px-3 py-1.5 rounded">
                <AlertTriangle size={13} className="text-[hsl(0_35%_45%)]" />
                <span className="font-mono uppercase tracking-[0.1em] text-[10px] text-[hsl(0_35%_45%)]">
                  Unverified · No chain of custody
                </span>
              </div>
            </div>

            <div className="bg-[hsl(var(--brand-bone))] border border-[hsl(var(--brand-sand))] rounded overflow-hidden shadow-sm">
              {/* Outlook chrome */}
              <div className="bg-[hsl(var(--brand-sand))]/40 border-b border-[hsl(var(--brand-sand))] px-4 py-2.5 flex items-center justify-between">
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-[hsl(var(--brand-slate))]/30" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[hsl(var(--brand-slate))]/30" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[hsl(var(--brand-slate))]/30" />
                </div>
                <span className="text-[11px] font-medium text-[hsl(var(--brand-slate))]">Inbox — Outlook</span>
                <div className="w-10" />
              </div>
              <div className="bg-[hsl(var(--brand-bone))] border-b border-[hsl(var(--brand-sand))] px-4 py-2 flex items-center gap-4 text-[12px] text-[hsl(var(--brand-slate))]">
                <span className="font-medium text-[hsl(var(--brand-charcoal))]">Home</span>
                <span>View</span>
                <span>Help</span>
              </div>

              {/* Email body */}
              <div className="p-6 lg:p-8 bg-white">
                <div className="border-b border-[hsl(var(--brand-sand))] pb-5 mb-5">
                  <h3 className="text-lg font-medium text-[hsl(var(--brand-charcoal))] mb-3">
                    Updated banking details — please action
                  </h3>
                  <div className="flex justify-between items-start gap-3 flex-wrap">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-[hsl(var(--brand-sand))] text-[hsl(var(--brand-slate))] flex items-center justify-center font-medium text-xs shrink-0">
                        OR
                      </div>
                      <div>
                        <div className="text-sm font-medium text-[hsl(var(--brand-charcoal))]">
                          Olivia Reed{" "}
                          <span className="text-[hsl(0_35%_45%)] text-xs font-normal">
                            &lt;olivia.reed.accounts+9382@gmail.com&gt;
                          </span>
                        </div>
                        <div className="text-xs text-[hsl(var(--brand-slate))] mt-0.5">To: accounts@yourcompany.com.au</div>
                      </div>
                    </div>
                    <span className="text-xs text-[hsl(var(--brand-slate))] shrink-0">10:42 AM</span>
                  </div>
                </div>

                <div className="inline-flex items-center gap-2 border border-[hsl(var(--brand-sand))] rounded px-3 py-2 mb-5 bg-[hsl(var(--brand-bone))]/50">
                  <Mail size={15} className="text-[hsl(var(--brand-slate))]" />
                  <span className="text-xs font-medium text-[hsl(var(--brand-charcoal))]">Updated_Bank_Details.pdf</span>
                  <span className="text-[10px] text-[hsl(var(--brand-slate))]">· 84 KB</span>
                </div>

                <div className="text-sm text-[hsl(var(--brand-slate))] leading-relaxed space-y-3">
                  <p>Hi team,</p>
                  <p>As discussed please update my banking details using the attached form for the upcoming settlement.</p>
                  <p>Many thanks,<br />Olivia</p>
                </div>
              </div>
            </div>

            {/* Red-flag callouts below the email */}
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
              {[
                { label: "Gmail sender", sub: "Not your client's domain" },
                { label: "PDF attachment", sub: "No audit trail" },
                { label: "No ID check", sub: "Anyone could send this" },
              ].map(({ label, sub }) => (
                <div key={label} className="rounded border border-[hsl(0_35%_45%)]/15 bg-[hsl(0_35%_45%)]/5 px-3 py-2">
                  <div className="text-[11px] font-semibold text-[hsl(0_35%_45%)]">{label}</div>
                  <div className="text-[10px] text-[hsl(var(--brand-slate))] mt-0.5 leading-snug">{sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* ConnectID — The Solution */}
      <Section className="!py-20 border-t border-[hsl(var(--brand-sand))] bg-[hsl(var(--brand-bone))]">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <Eyebrow>The solution</Eyebrow>
            <h2 className="mt-3 text-3xl sm:text-4xl font-semibold text-[hsl(var(--brand-charcoal))] leading-tight">
              ConnectID — simple, secure identity verification.
            </h2>
            <p className="mt-5 text-lg text-[hsl(var(--brand-slate))] leading-relaxed">
              ConnectID® is a digital identity solution that facilitates secure
              data exchange between your customer's bank and FortifyID.
            </p>
            <div className="mt-8">
              <img
                src={CONNECTID_LOGO}
                alt="ConnectID"
                style={{ height: 36, width: "auto" }}
                className="shrink-0 opacity-85"
              />
            </div>
            <div className="mt-8 flex flex-col gap-3">
              {[
                "Customers verify using their bank. No identity documents, no new account or sign up required.",
                "Customers must provide their consent before any personal information to be shared.",
                "Every verification creates an audit trail which can be stored in your systems to aid compliance.",
              ].map((point) => (
                <div key={point} className="flex items-start gap-3">
                  <ShieldCheck className="w-5 h-5 text-[hsl(var(--brand-forest))] mt-0.5 shrink-0" />
                  <p className="text-sm text-[hsl(var(--brand-slate))] leading-relaxed">
                    {point}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-center">
            <ConnectIDFlow />
          </div>
        </div>
      </Section>

      {/* Beyond fraud — operational benefits */}
      <Section className="!py-20 border-t border-[hsl(var(--brand-sand))]">
        <div className="max-w-2xl mb-12">
          <Eyebrow>More than fraud prevention</Eyebrow>
          <h2 className="mt-3 text-3xl sm:text-4xl font-semibold text-[hsl(var(--brand-charcoal))]">
            An operational upgrade for your whole team.
          </h2>
          <p className="mt-4 text-lg text-[hsl(var(--brand-slate))] leading-relaxed">
            FortifyID isn't just a security layer — it replaces a messy tangle
            of emails, PDFs, and phone calls with a structured, auditable
            workflow that makes life easier for staff and clients alike.
          </p>
        </div>
        <OperationalBenefits />
      </Section>

      {/* Industries we serve */}
      <Section className="!py-16 border-t border-[hsl(var(--brand-sand))]">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <Eyebrow>Industries we serve</Eyebrow>
            <h2 className="mt-3 text-3xl sm:text-4xl font-semibold text-[hsl(var(--brand-charcoal))] max-w-2xl">
              One product. Built for the way your industry actually works.
            </h2>
          </div>
          <Link
            href="/industries"
            className="text-sm font-medium text-[hsl(var(--brand-forest))] hover:underline inline-flex items-center gap-1"
            data-testid="link-industries-all"
          >
            All industries <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[hsl(var(--brand-sand))] border border-[hsl(var(--brand-sand))] rounded-xl overflow-hidden">
          {INDUSTRIES.map((i) => {
            const Icon = INDUSTRY_ICONS[i.slug] ?? Building2;
            return (
              <Link
                key={i.slug}
                href={`/industries/${i.slug}`}
                className="bg-[hsl(var(--brand-bone))] p-6 sm:p-8 hover:bg-white transition-colors group"
                data-testid={`card-home-industry-${i.slug}`}
              >
                <div className="w-10 h-10 rounded bg-[hsl(var(--brand-forest))] text-[hsl(var(--brand-brass))] flex items-center justify-center">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="mt-5 text-lg font-semibold text-[hsl(var(--brand-charcoal))]">
                  {i.name}
                </h3>
                <p className="mt-2 text-sm text-[hsl(var(--brand-slate))] leading-relaxed">
                  {i.shortBlurb}
                </p>
                <div className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[hsl(var(--brand-forest))] group-hover:gap-2 transition-all">
                  Read more <ArrowRight className="w-4 h-4" />
                </div>
              </Link>
            );
          })}
        </div>
        <div className="mt-4 text-sm text-[hsl(var(--brand-slate))]">
          Industry not listed? See the{" "}
          <Link
            href="/industries/generic"
            className="underline hover:text-[hsl(var(--brand-forest))]"
          >
            generic outbound-payments page
          </Link>{" "}
          — {GENERIC_INDUSTRY.shortBlurb.toLowerCase()}
        </div>
      </Section>

      {/* CTA */}
      <Section>
        <div className="rounded-2xl bg-[hsl(var(--brand-forest))] text-[hsl(var(--brand-bone))] p-8 sm:p-12 md:p-16 grid md:grid-cols-3 gap-10 items-center">
          <div className="md:col-span-2">
            <Eyebrow>
              <span className="text-[hsl(var(--brand-brass))]">
                Ready to start
              </span>
            </Eyebrow>
            <h2 className="mt-3 text-3xl sm:text-4xl font-semibold leading-tight">
              Enable the secure change-request flow in your business this week.
            </h2>
            <p className="mt-4 text-[hsl(var(--brand-sand))] max-w-xl">
              Set up your workspace, configure (or enable) ConnectID, brand your
              client surface, and start sending verified requests.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <a
              href={APP_SIGN_UP_URL}
              className="text-center bg-[hsl(var(--brand-brass))] text-[hsl(var(--brand-charcoal))] px-6 py-3 rounded font-medium hover:bg-[hsl(42,55%,52%)] transition-colors"
              data-testid="button-cta-signup"
            >
              Create your workspace
            </a>
            <Link
              href="/contact"
              className="text-center border border-[hsl(var(--brand-sand))]/30 px-6 py-3 rounded font-medium hover:bg-white/5 transition-colors"
            >
              Talk to us
            </Link>
          </div>
        </div>
      </Section>
    </>
  );
}
